import { useEffect, useRef, useState } from "react";
import { GraduationCap, Plus } from "lucide-react";
import { EducationCard } from "./EducationCard";
import { EntryRail } from "./EntryRail";
import type { EducationEntry } from "./types";
import { hasIssues, validateEducationEntry, visibleErrors } from "./validation";

interface EducationStepProps {
  items: EducationEntry[];
  onAdd: () => void;
  onUpdate: (id: string, field: keyof EducationEntry, value: string) => void;
  onRemove: (id: string) => void;
  /** Also show "required" / "too short" messages (after a Continue attempt). */
  showAllErrors?: boolean;
}

const isIncomplete = (edu: EducationEntry) => hasIssues(validateEducationEntry(edu));

export function EducationStep({
  items,
  onAdd,
  onUpdate,
  onRemove,
  showAllErrors = false,
}: EducationStepProps) {
  // Master–detail: one entry's form at a time (see ExperienceStep).
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);
  const prevLen = useRef(items.length);

  useEffect(() => {
    const len = items.length;
    if (len > prevLen.current) {
      setActiveId(items[len - 1]?.id ?? null);
    } else if (len === 0) {
      setActiveId(null);
    } else if (!items.some((i) => i.id === activeId)) {
      setActiveId(items[0]?.id ?? null);
    }
    prevLen.current = len;
  }, [items, activeId]);

  if (items.length === 0) {
    return (
      <div
        className="flex flex-col items-center gap-3 rounded-2xl border border-dashed p-10 text-center"
        style={{ borderColor: "var(--landing-glass-border)" }}
      >
        <div
          className="flex h-12 w-12 items-center justify-center rounded-xl"
          style={{ background: "rgba(var(--acc2-rgb),0.1)", border: "1px solid rgba(var(--acc2-rgb),0.2)" }}
        >
          <GraduationCap className="h-5 w-5 text-[var(--acc2)]" />
        </div>
        <p className="text-sm font-medium" style={{ color: "var(--landing-text)" }}>
          Education is optional
        </p>
        <p className="max-w-xs text-xs" style={{ color: "var(--landing-text-muted)" }}>
          Add a degree or program if it strengthens your application. You can skip this step otherwise.
        </p>
        <button
          type="button"
          onClick={onAdd}
          className="mt-1 inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-dashed px-5 text-sm font-semibold transition-colors hover:border-[var(--acc2)]/50 hover:text-[var(--acc2)]"
          style={{ borderColor: "var(--landing-glass-border)", color: "var(--landing-text-secondary)" }}
        >
          <Plus className="h-4 w-4" />
          Add education
        </button>
      </div>
    );
  }

  const active = items.find((i) => i.id === activeId) ?? items[0];
  const activeIndex = items.findIndex((i) => i.id === active.id);
  const activeIssues = validateEducationEntry(active);

  return (
    <div className="space-y-4">
      <EntryRail
        items={items.map((it) => ({
          id: it.id,
          title: it.institution || it.degree || "New entry",
          subtitle: it.fieldOfStudy || it.degree || "—",
          incomplete: isIncomplete(it),
        }))}
        activeId={active.id}
        accent="var(--acc2)"
        addLabel="Add education"
        onSelect={setActiveId}
        onAdd={onAdd}
      />

      <EducationCard
        index={activeIndex}
        item={active}
        incomplete={hasIssues(activeIssues)}
        errors={visibleErrors(activeIssues, showAllErrors)}
        onUpdate={(field, value) => onUpdate(active.id, field, value)}
        onRemove={() => onRemove(active.id)}
      />
    </div>
  );
}
